import TooltipWrapper from '@/components/main/Tooltip'
import { Button } from '@/components/ui/button'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { X } from 'lucide-react'
import { Role } from '@/types/user'
import { OnboardingInvitee, OnboardingStep } from '../types'

interface OnboardingStepsProps {
    step: OnboardingStep
    stepIndex: number
    totalSteps: number
    selectedOptions: string[]
    textResponse: string
    inviteDraft: string
    invitees: OnboardingInvitee[]
    progress: number
    primaryActionLabel: string
    showPrevious: boolean
    isTextStep: boolean
    isInviteStep: boolean
    onOptionToggle: (optionId: string) => void
    onTextChange: (value: string) => void
    onInviteDraftChange: (value: string) => void
    onAddInvite: () => void
    onRemoveInvite: (email: string) => void
    onInviteRoleChange: (email: string, roleId: string) => void
    onPrevious: () => void
    onPrimaryAction: () => void | Promise<void>
    onWorkspaceSave?: () => void
    isSavingWorkspace?: boolean
    isPrimaryActionLoading?: boolean
    otherInputValue?: string
    onOtherInputChange?: (value: string) => void
    workspaceRoles?: Role[]
}

const getRoleDescription = (roleName: string) => {
    if (roleName === 'user') {
        return 'The standard role with access limited to basic app functionalities. Team members can work within permissions granted by an Admin or Owner.'
    }
    if (roleName === 'admin') {
        return 'Admins manage day-to-day operations, including creating content and managing team members (except Owners).'
    }
    return 'Owners have full control, including managing workspace settings and team management.'
}

const ROLE_DISPLAY_LABELS: Record<string, string> = {
    user: 'Team Member',
    admin: 'Admin',
    owner: 'Owner',
}

export const OnboardingSteps = ({
    step,
    stepIndex,
    totalSteps,
    selectedOptions,
    textResponse,
    inviteDraft,
    invitees,
    progress,
    primaryActionLabel,
    showPrevious,
    isTextStep,
    isInviteStep,
    onOptionToggle,
    onTextChange,
    onInviteDraftChange,
    onAddInvite,
    onRemoveInvite,
    onInviteRoleChange,
    onPrevious,
    onPrimaryAction,
    onWorkspaceSave,
    isSavingWorkspace,
    isPrimaryActionLoading,
    otherInputValue,
    onOtherInputChange,
    workspaceRoles = [],
}: OnboardingStepsProps) => {
    const showOtherInput = step.hasOtherOption && selectedOptions.includes('other-specify')
    const isWorkspaceNameStep = step.id === 'workspace-name'

    return (
        <div className='px-10 pt-8 pb-10'>
            <div className='min-h-[300px] space-y-6'>
                <h2 className='text-2xl font-semibold text-violet-deepest'>{step.question}</h2>
                {step.subtitle ? <p className='text-sm text-violet-muted'>{step.subtitle}</p> : null}

                {!isTextStep && !isInviteStep && step.options?.length ? (
                    <div className='space-y-4'>
                        <div className='flex flex-wrap gap-4'>
                            {step.options.map(option => {
                                const isSelected = selectedOptions.includes(option.id)
                                return (
                                    <span
                                        key={option.id}
                                        onClick={() => onOptionToggle(option.id)}
                                        className={`rounded-lg border-[1.5px] cursor-pointer px-5 py-3 text-sm font-medium transition-all ${
                                            isSelected
                                                ? 'border-stroke-brand bg-violet-whisper text-violet-deep shadow-[0_6px_18px_rgba(106,48,118,0.2)]'
                                                : 'border-stroke-base bg-white text-neutral-charcoal hover:border-violet-wash hover:bg-violet-glow'
                                        }`}
                                    >
                                        {option.label}
                                    </span>
                                )
                            })}
                        </div>
                        {showOtherInput && (
                            <div className='max-w-2xl'>
                                <input
                                    type='text'
                                    value={otherInputValue || ''}
                                    onChange={e => onOtherInputChange?.(e.target.value)}
                                    placeholder='Please specify...'
                                    className='w-full px-5 py-3 text-base bg-white border rounded-xl border-stroke-brand text-neutral-ink placeholder:text-violet-soft focus:border-violet-accent focus:outline-none focus:ring-2 focus:ring-violet-ring'
                                    autoFocus
                                />
                            </div>
                        )}
                    </div>
                ) : null}

                {isTextStep ? (
                    <div className='max-w-2xl space-y-3'>
                        <input
                            type='text'
                            value={textResponse}
                            onChange={event => onTextChange(event.target.value)}
                            placeholder={step.inputPlaceholder}
                            className='w-full px-5 py-3 text-base bg-white border rounded-xl border-stroke-brand text-neutral-ink placeholder:text-violet-soft focus:border-violet-accent focus:outline-none focus:ring-2 focus:ring-violet-ring'
                        />
                        {isWorkspaceNameStep && textResponse && (
                            <Button
                                type='button'
                                variant='outline'
                                onClick={onWorkspaceSave}
                                disabled={isSavingWorkspace}
                                className='px-5 py-2 text-sm font-medium'
                            >
                                {isSavingWorkspace ? 'Saving...' : 'Save Workspace Name'}
                            </Button>
                        )}
                    </div>
                ) : null}

                {isInviteStep ? (
                    <div className='space-y-3'>
                        <div className='max-w-2xl space-y-2'>
                            <input
                                type='email'
                                value={inviteDraft}
                                onChange={event => onInviteDraftChange(event.target.value)}
                                onKeyDown={event => {
                                    if (event.key === 'Enter') {
                                        event.preventDefault()
                                        onAddInvite()
                                    }
                                }}
                                placeholder={step.inputPlaceholder}
                                className='w-full px-5 py-3 text-base bg-white border rounded-xl border-stroke-brand text-neutral-ink placeholder:text-violet-soft focus:border-violet-accent focus:outline-none focus:ring-2 focus:ring-violet-ring'
                            />
                            {inviteDraft.trim() ? (
                                <span
                                    onMouseDown={event => event.preventDefault()}
                                    onClick={onAddInvite}
                                    className='flex items-center gap-2 px-4 py-2 text-sm font-medium transition-colors bg-white border shadow-sm cursor-pointer w-fit rounded-xl border-stroke-subtle text-violet-emphasis hover:bg-violet-veil'
                                >
                                    <span className='text-lg leading-none'>＋</span>
                                    {`Add "${inviteDraft.trim()}"`}
                                </span>
                            ) : null}
                        </div>

                        {invitees.length ? (
                            <div className='max-h-[220px] space-y-2 overflow-y-auto border border-stroke-subtle rounded-xl p-3 bg-white'>
                                {invitees.map(invite => (
                                    <div
                                        key={invite.email}
                                        className='flex flex-col gap-3 rounded-lg border border-stroke-subtle bg-violet-whisper/40 px-3 py-3 text-sm font-medium text-neutral-midnight md:flex-row md:items-center md:justify-between'
                                    >
                                        <span className='truncate font-semibold text-violet-deep'>{invite.email}</span>
                                        <div className='flex items-center gap-3'>
                                            <Select
                                                value={invite.role || undefined}
                                                onValueChange={value => onInviteRoleChange(invite.email, value)}
                                            >
                                                <SelectTrigger className='w-[180px]'>
                                                    <SelectValue placeholder='Select role' />
                                                </SelectTrigger>
                                                <SelectContent className='z-[1100]'>
                                                    {workspaceRoles.map(role => (
                                                        <div className='flex flex-col' key={role.id}>
                                                            <TooltipWrapper content={getRoleDescription(role.name)}>
                                                                <SelectItem value={role.id} className='cursor-pointer'>
                                                                    {ROLE_DISPLAY_LABELS[role.name] || role.name}
                                                                </SelectItem>
                                                            </TooltipWrapper>
                                                        </div>
                                                    ))}
                                                </SelectContent>
                                            </Select>
                                            <Button
                                                type='button'
                                                size='sm'
                                                variant='ghost'
                                                onClick={() => onRemoveInvite(invite.email)}
                                                className='text-violet-pastel hover:text-magenta-bright'
                                            >
                                                <X size={16} />
                                            </Button>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        ) : null}
                        {!invitees.length && (
                            <p className='text-xs text-violet-muted'>Add teammates now or skip and do this later.</p>
                        )}
                    </div>
                ) : null}
            </div>

            <div className='flex flex-col gap-4 mt-10'>
                <div className='flex items-center justify-between text-sm font-medium text-violet-emphasis'>
                    <span>{`Step ${stepIndex + 1} of ${totalSteps}`}</span>
                </div>
                <div className='relative w-full h-2 overflow-hidden rounded-full bg-surface-raised'>
                    <div
                        className='absolute inset-y-0 left-0 rounded-full bg-violet-base'
                        style={{ width: `${progress}%` }}
                    />
                </div>
                <div className='flex items-center justify-between pt-2'>
                    <Button
                        type='button'
                        variant='outline'
                        onClick={onPrevious}
                        disabled={!showPrevious}
                        className={`rounded-lg px-5 py-2 text-sm font-medium transition-colors ${
                            showPrevious
                                ? 'text-violet-primary hover:bg-violet-veil'
                                : 'cursor-not-allowed text-violet-tint'
                        }`}
                    >
                        Back
                    </Button>

                    <div className='flex items-center gap-3'>
                        <Button
                            type='button'
                            variant='brand'
                            onClick={onPrimaryAction}
                            loading={isPrimaryActionLoading}
                            disabled={isPrimaryActionLoading}
                            className='flex items-center gap-2 px-6 py-2 text-sm font-semibold text-white transition-colors rounded-lg bg-violet-base hover:bg-magenta-vivid'
                        >
                            {primaryActionLabel}
                        </Button>
                    </div>
                </div>
            </div>
        </div>
    )
}
